import React, { Fragment, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Menu, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  CheckBadgeIcon,
  ClockIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { TWModal, TWButton } from '../../../components/ui';
import termAcceptanceApi from '../../../services/termAcceptanceApi';
import termsApi from '../../../services/termsApi';

dayjs.extend(utc);

const cn = (...c) => c.filter(Boolean).join(' ');

const fmtSigned = (v) => dayjs.utc(v).format('MMM D, YYYY · HH:mm');

// agreement: { name, termName }. Önce kullanıcının imzaladığı kopyayı (Mine)
// çeker; kayıt yoksa şablona (Terms/GetByName) düşer. Salt görüntüleme.
// Aynı company için birden çok kabul kaydı varsa tarih bazlı sürüm seçici çıkar.
const AgreementViewModal = ({ open, agreement, companyId, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [template, setTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!open || !agreement?.termName) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    setDocs([]);
    setTemplate('');
    setSelectedIdx(0);

    termAcceptanceApi
      .mine(agreement.termName, companyId)
      .then((res) => {
        if (!active) return null;
        let items = (res.data?.data || []).filter((d) => d.merchanttermhtml);
        if (companyId) {
          items = items.filter((d) => d.companyid === companyId);
        }
        // En yeni imza önce.
        items = items.sort(
          (a, b) => new Date(b.acceptedat) - new Date(a.acceptedat),
        );
        // latestOnly dokümanlarda (ör. merchant terms) yalnızca en son imzalı
        // kopya kalır; eski sürümler ve sürüm seçici gizlenir.
        if (agreement.latestOnly) {
          items = items.slice(0, 1);
        }
        if (items.length > 0) {
          setDocs(items);
          return null;
        }
        return termsApi.getByName(agreement.termName).then((tRes) => {
          if (active) setTemplate(tRes.data?.data?.description || '');
        });
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, agreement, companyId]);

  const multiCompany =
    docs.length > 1 && new Set(docs.map((d) => d.companyid)).size > 1;

  const optionLabel = (d) =>
    `${multiCompany && d.companyname ? `${d.companyname} · ` : ''}${fmtSigned(
      d.acceptedat,
    )}`;

  const VersionBar = () => {
    const current = docs[selectedIdx];
    const isLatest = selectedIdx === 0;
    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3 dark:border-slate-700/60">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
          <CheckBadgeIcon className="h-4 w-4" />
          Signed on {fmtSigned(current?.acceptedat)} UTC
          {isLatest && docs.length > 1 ? (
            <span className="ml-1 rounded bg-emerald-600/90 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-white">
              Latest
            </span>
          ) : null}
        </div>

        {docs.length > 1 ? (
          <Menu as="div" className="relative">
            <Menu.Button className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600">
              <ClockIcon className="h-4 w-4 text-slate-400" />
              {docs.length} signed copies
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-1 max-h-72 w-72 origin-top-right overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-800">
                {docs.map((d, i) => (
                  <Menu.Item key={d.id || i}>
                    {({ active: hover }) => (
                      <button
                        type="button"
                        onClick={() => setSelectedIdx(i)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs',
                          hover ? 'bg-slate-50 dark:bg-slate-700/50' : '',
                          i === selectedIdx
                            ? 'font-semibold text-blue-600 dark:text-sky-400'
                            : 'text-slate-600 dark:text-slate-300',
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          {i === selectedIdx ? (
                            <CheckIcon className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <span className="w-3.5 shrink-0" />
                          )}
                          <span className="truncate tabular-nums">
                            {optionLabel(d)}
                          </span>
                        </span>
                        {i === 0 ? (
                          <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Latest
                          </span>
                        ) : null}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex h-48 items-center justify-center">
          <Spin />
        </div>
      );
    }
    if (error) {
      return (
        <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          This document is not available right now. Please try again later.
        </p>
      );
    }

    if (docs.length === 0) {
      // İmzalı kayıt yok -> güncel şablon.
      return (
        <div>
          <p className="mb-3 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            No signed copy on file — showing the current agreement template.
          </p>
          {template ? (
            <div
              className="prose prose-sm max-h-[56vh] max-w-none overflow-y-auto pr-1 dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: template }}
            />
          ) : (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              No content available for this document.
            </p>
          )}
        </div>
      );
    }

    const current = docs[selectedIdx] || docs[0];
    return (
      <div>
        <VersionBar />
        <div
          className="prose prose-sm max-h-[52vh] max-w-none overflow-y-auto pr-1 dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: current.merchanttermhtml }}
        />
      </div>
    );
  };

  return (
    <TWModal
      open={open}
      onClose={onClose}
      title={agreement?.name || 'Agreement'}
      size="3xl"
      showCloseButton
      footer={
        <div className="flex justify-end">
          <TWButton variant="primary" label="Close" onClick={onClose} />
        </div>
      }
    >
      {renderContent()}
    </TWModal>
  );
};

export default AgreementViewModal;
